import { useRef, useState, useEffect } from "react";
import { ExpandedFormData } from "../data/models";
import { sendEmail } from "../functions/services/sendEmail";

import {
  fields2,
  //fields3,
  conditionFields1,
  errorOrder,
  dealerList,
} from "../data/models/FormFields";
import {
  vehicleImages,
  imagesOrderOffer,
  imagesOrderTestDrive,
} from "../data/models/images";
import { isValidInput } from "../functions/utils";
import SyncLoader from "react-spinners/SyncLoader";
import Warning from "../assets/svg/warning.svg";

import Button from "../components/Button";
import Conditions from "../components/Conditions";
import FormSection from "../components/FormSection";
import Image from "../components/Image";
import SelectDealerMap from "../components/SelectDealerMap";
import DropdownField from "../components/DropdownField";

const modelOptionsOffer = imagesOrderOffer.map((key) => ({
  id: key,
  label: key,
}));
const modelOptionsTestDrive = imagesOrderTestDrive.map((key) => ({
  id: key,
  label: key,
}));

const RequestPage = () => {
  const mode = "Ponuda";
  const [loading, setLoading] = useState<boolean>(false);
  const [offerToggle, setOfferToggle] = useState<string>(mode);

  const vehicleModelField = {
    name: "Model vozila",
    placeholder: "Odaberite model",
    required: true,
    type: "dropdown",
    options:
      offerToggle === "Ponuda" ? modelOptionsOffer : modelOptionsTestDrive,
  };
  const [modelToggle, setModelToggle] = useState<string>("");

  const [formData, setFormData] = useState<ExpandedFormData>({
    general: {},
    vehicle: {},
    conditions: [],
  });
  const [errors, setErrors] = useState<string[]>([]);
  const inputRefs = useRef<{ [key: string]: HTMLElement | null }>({}); // used for error scrolling

  useEffect(() => {
    const options =
      offerToggle === "Ponuda" ? modelOptionsOffer : modelOptionsTestDrive;

    const stillExists = options.some((opt) => opt.id === modelToggle);

    // Only reset when the previously selected model is no longer offered -
    // never auto-select a model on the user's behalf.
    if (modelToggle && !stillExists) {
      setModelToggle("");
    }
  }, [offerToggle, modelToggle]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      vehicle: {
        ...prev.vehicle,
        "Auto centar": prev.vehicle["Auto centar"] ?? dealerList[0].id,
      },
    }));
  }, []);

  const handleFormChange = (section: string, values: any) => {
    setFormData((prev) => {
      if (section === "conditions") {
        return { ...prev, conditions: values };
      }

      return {
        ...prev,
        [section]: {
          ...(prev as any)[section],
          ...values,
        },
      };
    });
  };

  const handleSubmit = async () => {
    //const requiredFields = [...fields2, ...fields3];
    const requiredFields = [...fields2];
    const allConditionFields = [...conditionFields1];

    // Find not filled out required fields
    const missingFields = requiredFields.filter(
      (field) =>
        field.required &&
        !formData.general[field.name] &&
        !formData.vehicle[field.name],
    );
    allConditionFields.forEach((field) => {
      if (field.required && !formData.conditions.includes(field.id)) {
        const missingField = {
          name: field.id,
          placeholder: "",
          required: true,
        };
        requiredFields.push(missingField);
        missingFields.push(missingField);
      }
    });

    if (!modelToggle) {
      missingFields.push({
        name: "Model vozila",
        placeholder: "",
        required: true,
      });
    }

    let validationErrors: string[] = [];
    validationErrors = missingFields.map((field) => field.name); // for error scrolling

    if (
      missingFields.length > 0 ||
      !isValidInput(formData.general["Email adresa"], "email") ||
      !isValidInput(formData.general["Telefonski broj"], "phone") ||
      !isValidInput(formData.general["Poštanski broj"], "postalCode")
    ) {
      setErrors(missingFields.map((field) => field.name));

      if (!isValidInput(formData.general["Email adresa"], "email")) {
        validationErrors.push("Email adresa");
      }
      if (!isValidInput(formData.general["Telefonski broj"], "phone")) {
        validationErrors.push("Telefonski broj");
      }
      if (!isValidInput(formData.general["Poštanski broj"], "postalCode")) {
        validationErrors.push("Poštanski broj");
      }

      validationErrors = sortValidationErrors(validationErrors);
      setErrors(validationErrors);

      // If there are errors, scroll to the first error
      if (validationErrors.length > 0) {
        const firstErrorField = validationErrors[0];
        const errorElement = inputRefs.current[firstErrorField];

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      }

      return;
    }

    setLoading(true);
    const dealerId: number | undefined = (formData.vehicle["Auto centar"] ??
      dealerList[0].id) as number;

    const mergedFormData: ExpandedFormData = {
      ...formData,
      vehicle: {
        "Auto centar": dealerId,
        "Broj šasije": modelToggle,
        "Model vozila": modelToggle,
      },
    };

    try {
      await sendEmail({ formData: mergedFormData, mode: offerToggle });
      setFormData({
        general: {},
        vehicle: {},
        conditions: [],
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const sortValidationErrors = (errors: string[]) => {
    return errors.sort((a, b) => errorOrder.indexOf(a) - errorOrder.indexOf(b));
  };

  return (
    <div className="flex flex-col bg-black">
      <div className="flex flex-col gap-6 px-6 pb-6 pt-10 self-center md:max-w-[570px] w-full">
        {/* Header */}
        <div className="flex flex-col items-center justify-between gap-5 sm:flex-row sm:gap-0">
          <h1 className="self-start text-lg font-bold text-white sm:self-auto sm:text-xl">
            {offerToggle === "Ponuda"
              ? "ZATRAŽITE PONUDU"
              : "ZATRAŽITE TESTNU VOŽNJU"}
          </h1>
          <div className="flex gap-1 p-1 w-full sm:w-[256px] border border-extra-light rounded">
            <Button
              text={"Ponuda"}
              variant={offerToggle === "Ponuda" ? "black" : "gray"}
              action={() => setOfferToggle("Ponuda")}
            ></Button>
            <Button
              text={"Testna vožnja"}
              variant={offerToggle === "Testna vožnja" ? "black" : "gray"}
              action={() => setOfferToggle("Testna vožnja")}
            ></Button>
          </div>
        </div>
        <hr className="w-full border-t-1 border-extra-light" />

        {/* Body */}
        <div className="flex flex-col gap-6 ">
          <div className="flex flex-col gap-6">
            <FormSection
              title="Općenito"
              subtitle={
                offerToggle === "Ponuda"
                  ? "Unesite podatke za zahtjev ponude."
                  : "Unesite podatke za zahtjev testne vožnje."
              }
              fields={fields2}
              columns={1}
              data={formData.general}
              onChange={(values) => handleFormChange("general", values)}
              setErrors={setErrors}
              errors={errors}
              inputRefs={inputRefs}
              theme="black"
            ></FormSection>
            <hr className="w-full border-t-1 border-extra-light" />

            <FormSection
              title="O vozilu"
              //fields={fields3}
              data={formData.vehicle}
              onChange={(values) => handleFormChange("vehicle", values)}
              setErrors={setErrors}
              errors={errors}
              inputRefs={inputRefs}
              childrenOrder="front"
              theme="black"
            >
              <div
                ref={(el) => (inputRefs.current["Model vozila"] = el)}
                tabIndex={-1}
                className="flex flex-col gap-2 outline-none"
              >
                <label className="text-sm font-medium text-white">
                  Model vozila
                  <span className="pl-1 text-sm text-warning-red">*</span>
                </label>
                <div className="w-full">
                  <DropdownField
                    field={vehicleModelField as any}
                    value={modelToggle}
                    onChange={(nextId) => {
                      if (nextId) {
                        setModelToggle(String(nextId));
                        setErrors((prev) =>
                          prev.filter((error) => error !== "Model vozila"),
                        );
                      }
                    }}
                    error={errors.includes("Model vozila")}
                  />
                </div>
                {errors.includes("Model vozila") && (
                  <div className="flex items-center gap-1 text-xs text-warning-red">
                    <img src={Warning} alt="Warning" />
                    Ovo polje je obavezno.
                  </div>
                )}
              </div>
              {modelToggle && (
                <Image image={vehicleImages[modelToggle]} size="sm"></Image>
              )}
              <SelectDealerMap
                initialId={formData.vehicle["Auto centar"] ?? dealerList[0].id}
                dealers={dealerList}
                onChange={(id /*, dealer? */) => {
                  setFormData((prev) => ({
                    ...prev,
                    vehicle: {
                      ...prev.vehicle,
                      "Auto centar": id,
                    },
                  }));
                }}
                theme="black"
              />
            </FormSection>
          </div>

          <hr className="w-full border-t-1 border-extra-light" />

          {/*Uvjeti */}
          <div className="flex flex-col gap-6">
            <Conditions
              title="DODATNO"
              // subtitle="Pročitajte dolje navedene informacije i odaberite što Vas zanima."
              fields={conditionFields1}
              selected={formData.conditions}
              onChange={(updatedConditions) =>
                handleFormChange("conditions", updatedConditions)
              }
              setErrors={setErrors}
              errors={errors}
              inputRefs={inputRefs}
              theme="black"
            />

            <hr className="w-full border-t-1 border-extra-light" />
            <Button
              text={
                loading
                  ? "Slanje zahtjeva"
                  : offerToggle === "Ponuda"
                    ? "Zatražite ponudu"
                    : "Zatražite testnu vožnju"
              }
              variant={"primary"}
              size={"base"}
              action={handleSubmit}
              icon={
                <SyncLoader
                  color={"white"}
                  loading={loading}
                  size={7}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />
              }
            ></Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestPage;
